import { LazyLog } from '@melloware/react-logviewer';
import { useEffect, useMemo, useRef } from 'react';
import { StyledInformationDialog } from './LogViewer.styles.tsx';
import { ExecutionNodeStatusEnum } from 'types.ts';
import { useStore } from 'store/store.ts';
import { filterLogsByLevel } from './filterLogsByLevel.ts';
import { renderLog } from './renderLog.tsx';

const BOTTOM_THRESHOLD = 24;

type LogViewerProps = {
  logs: string;
  status?: ExecutionNodeStatusEnum;
};

const LogViewer = ({ logs, status }: LogViewerProps) => {
  const lazyLogRef = useRef<LazyLog>(null);
  const isPinnedToBottomRef = useRef(true);
  const enabledLogLevels = useStore.use.enabledLogLevels();

  const visibleLogs = useMemo(() => filterLogsByLevel(logs, enabledLogLevels), [logs, enabledLogLevels]);
  const isFilteredEmpty = !!logs && !visibleLogs;

  const handleScroll = ({
    scrollTop,
    scrollHeight,
    clientHeight,
  }: {
    scrollTop: number;
    scrollHeight: number;
    clientHeight: number;
  }) => {
    if (scrollHeight > clientHeight) {
      isPinnedToBottomRef.current = scrollHeight - scrollTop - clientHeight <= BOTTOM_THRESHOLD;
    }
  };

  useEffect(() => {
    if (!isPinnedToBottomRef.current) return;

    const lineCount = visibleLogs ? visibleLogs.split('\n').length : 0;
    if (lineCount === 0) return;

    const frame = requestAnimationFrame(() => {
      lazyLogRef.current?.handleScrollToLine(lineCount);
    });

    return () => cancelAnimationFrame(frame);
  }, [visibleLogs]);

  if (isFilteredEmpty) {
    return (
      <StyledInformationDialog className="flex h-screen items-center justify-center px-4 text-center opacity-60">
        No logs match the selected levels.
      </StyledInformationDialog>
    );
  }

  return (
    <StyledInformationDialog className="h-screen">
      <LazyLog
        ref={lazyLogRef}
        text={visibleLogs}
        wrapLines
        enableSearch
        caseInsensitive
        selectableLines
        enableHotKeys
        loading={status === ExecutionNodeStatusEnum.RUNNING}
        onScroll={handleScroll}
        formatPart={renderLog}
        extraLines={1}
      />
    </StyledInformationDialog>
  );
};

export default LogViewer;
